import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getCategories, addCategory, updateCategory, deleteCategory } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    ArrowLeft, 
    Tags, 
    Plus, 
    Pencil, 
    Trash2,
    Loader2,
    Save
} from 'lucide-react';
import { toast } from 'sonner';

export default function CategoriesPage() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [newCategory, setNewCategory] = useState('');
    const [editCategory, setEditCategory] = useState({ old: '', new: '' });
    const [deleteTarget, setDeleteTarget] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/operator');
            return;
        }
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (error) {
            toast.error('Errore nel caricamento delle categorie');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = async () => {
        if (!newCategory.trim()) {
            toast.error('Inserisci un nome per la categoria');
            return;
        }

        setSubmitting(true);
        try {
            const response = await addCategory(newCategory.trim());
            setCategories(response.data.categories);
            setNewCategory('');
            setAddDialogOpen(false);
            toast.success('Categoria aggiunta');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta della categoria');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditCategory = async () => {
        if (!editCategory.new.trim()) {
            toast.error('Inserisci un nome per la categoria');
            return;
        }

        setSubmitting(true);
        try {
            const response = await updateCategory(editCategory.old, editCategory.new.trim());
            setCategories(response.data.categories);
            setEditDialogOpen(false);
            toast.success('Categoria aggiornata');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nella modifica della categoria');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteCategory = async () => {
        setSubmitting(true);
        try {
            const response = await deleteCategory(deleteTarget);
            setCategories(response.data.categories);
            setDeleteDialogOpen(false);
            toast.success('Categoria eliminata');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione della categoria');
        } finally {
            setSubmitting(false);
        }
    };

    const openEditDialog = (category) => {
        setEditCategory({ old: category, new: category });
        setEditDialogOpen(true);
    };

    const openDeleteDialog = (category) => {
        setDeleteTarget(category);
        setDeleteDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/operator')}
                                className="text-slate-600"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Dashboard
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Card className="border-slate-200">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-xl flex items-center gap-2">
                                    <Tags className="w-5 h-5 text-indigo-500" />
                                    Gestione Categorie
                                </CardTitle>
                                <CardDescription className="mt-1">
                                    Aggiungi, modifica o elimina le categorie dei ticket
                                </CardDescription>
                            </div>
                            <Button
                                onClick={() => setAddDialogOpen(true)}
                                className="bg-indigo-500 hover:bg-indigo-600"
                                data-testid="add-category-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Nuova Categoria
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {categories.map((category, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                    data-testid={`category-item-${index}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                                        <span className="font-medium text-slate-700">{category}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openEditDialog(category)}
                                            className="text-slate-600 hover:text-indigo-600"
                                            data-testid={`edit-category-${index}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openDeleteDialog(category)}
                                            className="text-slate-600 hover:text-red-600"
                                            data-testid={`delete-category-${index}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {categories.length === 0 && (
                                <div className="text-center py-8 text-slate-500">
                                    Nessuna categoria configurata
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </main>

            {/* Add Category Dialog */}
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nuova Categoria</DialogTitle>
                        <DialogDescription>
                            Inserisci il nome della nuova categoria per i ticket
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="new-category">Nome Categoria</Label>
                        <Input
                            id="new-category"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="Es: Assistenza Tecnica"
                            className="mt-2"
                            data-testid="new-category-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            onClick={handleAddCategory}
                            disabled={submitting}
                            className="bg-indigo-500 hover:bg-indigo-600"
                            data-testid="confirm-add-category"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Plus className="w-4 h-4 mr-2" />
                            )}
                            Aggiungi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Category Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Modifica Categoria</DialogTitle>
                        <DialogDescription>
                            Modifica il nome della categoria. I ticket esistenti verranno aggiornati automaticamente.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="edit-category">Nome Categoria</Label>
                        <Input
                            id="edit-category"
                            value={editCategory.new}
                            onChange={(e) => setEditCategory({ ...editCategory, new: e.target.value })}
                            className="mt-2"
                            data-testid="edit-category-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            onClick={handleEditCategory}
                            disabled={submitting}
                            className="bg-indigo-500 hover:bg-indigo-600"
                            data-testid="confirm-edit-category"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Salva
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Category Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questa categoria?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Stai per eliminare la categoria "<strong>{deleteTarget}</strong>". 
                            Questa azione non può essere annullata. La categoria può essere eliminata 
                            solo se non ci sono ticket associati.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteCategory}
                            className="bg-red-500 hover:bg-red-600"
                            disabled={submitting}
                            data-testid="confirm-delete-category"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
